var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable @typescript-eslint/no-floating-promises */
import { useState, useRef, useCallback, useEffect } from 'react';
import { floatTo16BitPCM, convertPCM16ToBase64 } from '../utils/audio';
/**
 * Hook for managing real-time audio recording
 *
 * @param onAudioChunk - the base url of the endpoint to connect to
 * @param analytics - pcln logger instance
 * @returns object containing any state we might need in the invoking component:
 * - `isRecording` - whether or not audio is currently being recorded
 * - `isMuted` - whether or not the audio recorder is muted
 * - `audioTrack` - the current audio track being processed
 * - `mediaStream` - the MediaStream object created for playback
 * - `audioContext` - the AudioContext object created on initialization
 * - `startRecording` - function to start recording audio
 * - `stopRecording` - function to stop recording audio
 * - `toggleMute` - function to toggle mute on/off
 */
export function useRealTimeAudioRecorder(onAudioChunk, analytics) {
  // State
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  // Refs for managing audio recording resources
  const mediaStreamRef = useRef(null);
  const audioTrackRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const startRecording = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    if (isRecording) return;
    try {
      // Request audio stream
      const stream = yield navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true
        }
      });
      mediaStreamRef.current = stream;
      // Set up audio track
      const track = stream.getAudioTracks()[0];
      audioTrackRef.current = track;
      // Create audio context
      const audioContext = new AudioContext({
        sampleRate: 24000
      });
      const mediaStreamSource = audioContext.createMediaStreamSource(stream);
      // Create filters for noise reduction
      const highPassFilter = audioContext.createBiquadFilter();
      highPassFilter.type = 'highpass';
      highPassFilter.frequency.value = 200;
      const lowPassFilter = audioContext.createBiquadFilter();
      lowPassFilter.type = 'lowpass';
      lowPassFilter.frequency.value = 4350;
      // Connect filters
      mediaStreamSource.connect(highPassFilter);
      highPassFilter.connect(lowPassFilter);
      // Create audio processor
      const processorCode = `
        class MyAudioProcessor extends AudioWorkletProcessor {
          constructor() {
            super()
            this.shouldProcess = true

            this.port.onmessage = (event) => {
              if (event.data.type === 'terminate') {
                this.shouldProcess = false
              }
            }
          }

          process(inputs, outputs, parameters) {
            if (!this.shouldProcess) {
              return false
            }
            const input = inputs[0]
            if (input && input.length > 0) {
              const channelData = input[0]
              this.port.postMessage(channelData.slice())
            }
            return true
          }
        }
        registerProcessor('my-audio-processor', MyAudioProcessor)
      `;
      // Load processor
      const blob = new Blob([processorCode], {
        type: 'application/javascript'
      });
      const url = URL.createObjectURL(blob);
      yield audioContext.audioWorklet.addModule(url);
      // Create and connect processor node
      const processorNode = new AudioWorkletNode(audioContext, 'my-audio-processor');
      lowPassFilter.connect(processorNode);
      processorNode.connect(audioContext.destination);
      // Set up message handler for audio processing
      processorNode.port.onmessage = event => {
        var _a;
        if (!((_a = audioTrackRef.current) === null || _a === void 0 ? void 0 : _a.enabled)) return;
        const float32Array = event.data;
        const pcm16Array = floatTo16BitPCM(float32Array);
        const base64String = convertPCM16ToBase64(pcm16Array);
        onAudioChunk(base64String);
      };
      // Store references
      processorRef.current = processorNode;
      audioContextRef.current = audioContext;
      setIsRecording(true);
    } catch (error) {
      analytics.logError({
        loggedFrom: 'useRealTimeAudioRecorder.startRecording',
        message: 'Failed to start recording user audio',
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }), [isRecording, onAudioChunk, analytics]);
  const stopRecording = useCallback(() => {
    // Reset mute state
    setIsMuted(false);
    // Stop and cleanup processor
    if (processorRef.current) {
      // Ensure that the audio processor immediately stops processing audio
      processorRef.current.port.postMessage({
        type: 'terminate'
      });
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    // Stop audio track
    if (audioTrackRef.current) {
      audioTrackRef.current.stop();
      audioTrackRef.current = null;
    }
    // Clear media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    setIsRecording(false);
  }, []);
  const toggleMute = useCallback(() => {
    if (audioTrackRef.current) {
      const newMuteState = !isMuted;
      audioTrackRef.current.enabled = !newMuteState;
      setIsMuted(newMuteState);
    }
  }, [isMuted]);
  useEffect(() => {
    return () => {
      // Clean up if component unmounts while recording
      if (isRecording) {
        stopRecording();
      }
    };
  }, [isRecording]);
  return {
    isRecording,
    isMuted,
    audioTrack: audioTrackRef.current,
    mediaStream: mediaStreamRef.current,
    audioContext: audioContextRef.current,
    startRecording,
    stopRecording,
    toggleMute
  };
}